import React, { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";

import {
  RentalStockItem,
  RentalStockListSortOrder,
  getRentalStockList,
  getStringFromRentalStockGoodsTypeId,
} from "../../../../../../services/rentalStockServices";

import useFullScreenLoading from "../../../../../../hooks/useFullScreenLoading";
import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../../../../../hooks/useInfiniteScroll";

import CommonSelect, {
  SelectOption,
} from "../../../../../../components/Select";
import { PostListNoPostIcon } from "../../../../../../components/Talk/PostList";
import CommonButton from "../../../../../../components/Button";
import RentalStockChangePopup from "../../../../../../components/RentalStock/RentalStockChangePopup";
import { RentalStockSummaryList } from "../../../../../../components/RentalStock/RentalStockSummaryList";

type Props = {
  storeId?: string;
  active: boolean;
};
export default function RentalStockChange({
  storeId,
  active,
}: Props): [{ RentalStockChangeReload: () => void }, React.ReactNode] {
  const [isLoading, setIsLoading] = useState<boolean>(active);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [popup, setPopup] = useState<string>("");
  const PAGE_SIZE = 10;
  const SCROLL_LOAD_START_PAGE = 2;

  const sortOption: SelectOption[] = [
    { label: "최근 등록 순", value: "createdAt.desc" },
    { label: "최근 재고 변경 순", value: "modifiedAt.desc" },
    { label: "분실/손상 많은 순", value: "lostDamagedCount.desc" },
  ];
  const [sort, setSort] = useState<SelectOption>(sortOption[0]);

  const [rentalStockList, setRentalStockList] = useState<RentalStockItem[]>([]);
  const [rentalStock, setRentalStock] = useState<RentalStockItem | undefined>(
    undefined
  );

  const loadRentalStockList = async (args?: InfiniteScrollCallbackArgs) => {
    let itemLength = INFINITE_CODE.ERROR;

    setIsLoading(true);
    try {
      const result = await getRentalStockList({
        sort: sort.value as RentalStockListSortOrder,
        storeId: storeId,
        ...args,
      });

      if (result.status === HttpStatusCode.Ok) {
        itemLength = result.data.items.length;
        if (args?.page === 1) {
          setRentalStockList(result.data.items);
        } else {
          setRentalStockList((prev) => [...(prev ?? []), ...result.data.items]);
        }
      }
    } catch (e) {
      setRentalStockList([]);
      console.log(e);
    } finally {
      setIsLoading(false);
    }

    return itemLength;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    isLoading,
    callback: loadRentalStockList,
    startPage: SCROLL_LOAD_START_PAGE,
    pageSize: PAGE_SIZE,
  });

  const handleRentalStockChange = (rentalStock: RentalStockItem) => {
    setRentalStock(rentalStock);
    setPopup("rentalStockChange");
  };

  const reloadAll = () => {
    resetScroll();
    loadRentalStockList({
      page: SCROLL_LOAD_START_PAGE - 1,
      pageSize: PAGE_SIZE,
    });
  };

  useEffect(() => {
    reloadAll();
  }, [sort, storeId, active]);

  return [
    { RentalStockChangeReload: reloadAll },
    <>
      {loadingComponent}
      <div className="px-5">
        <div className="py-5 flex justify-between items-center">
          <div className="flex flex-col text-lg font-medium">물품 목록</div>
          <div className="flex flex-col">
            <CommonSelect
              value={sort.value}
              optionList={sortOption}
              onChange={(option) => {
                setSort(option);
              }}
              className="border-none max-w-[200px] text-wrap text-right"
              optionWidth="180px"
              variant="transparent"
              optionOrder="right"
            />
          </div>
        </div>
        {rentalStockList && rentalStockList.length > 0 ? (
          <div className="flex flex-col gap-3 w-max-screen">
            {rentalStockList.map((item, index) => (
              <RentalStockListItem
                key={index}
                item={item}
                onClick={handleRentalStockChange}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
            <PostListNoPostIcon />
            등록된 물품이 없어요
          </div>
        )}
      </div>
      <RentalStockChangePopup
        popup={popup}
        item={rentalStock}
        onClose={() => {
          setPopup("");
          reloadAll();
        }}
      />
    </>,
  ];
}

export type RentalStockListItemProps = {
  item: RentalStockItem;
  onClick?: (item: RentalStockItem) => void;
};

export const RentalStockListItem = ({
  item,
  onClick,
}: RentalStockListItemProps) => {
  return (
    <div
      className="flex flex-col p-5 border border-brand-neutral-10 rounded-[10px] w-full h-full"
      style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.08)" }}
    >
      <div className="flex flex-row gap-2.5 justify-between items-center w-full">
        <div className="flex flex-col gap-2.5 w-full">
          <div className="flex flex-row h-[22px] text-[15px] font-medium leading-[22px] w-full">
            <div className="flex flex-col whitespace-nowrap ">
              [{getStringFromRentalStockGoodsTypeId(item.goodsTypeId)}]
            </div>
            &nbsp;
            <div className="flex flex-col max-w-[calc(100vw-185px)]">
              <div className="truncate">
                {item.goodsName} - {item.storeName}
              </div>
            </div>
          </div>
          <div className="flex flex-col h-[15px] text-xs text-brand-neutral-40 font-normal tracking-[-0.12px] leading-4 max-w-[calc(100vw-145px)]">
            <div className="truncate">{item.goodsDescription}</div>
          </div>
          <div className="flex flex-col">
            <RentalStockSummaryList summary={item.stockSummary} size="small" />
          </div>
          <CommonButton
            variant="tertiary"
            padding="default"
            className="min-w-[41px] rounded-[3px] text-brand-neutral-60 hidden design:block"
            onClick={() => {
              onClick?.(item);
            }}
          >
            <div className="text-sm font-medium whitespace-nowrap leading-[17px]">
              재고 변경
            </div>
          </CommonButton>
        </div>
        <CommonButton
          variant="tertiary"
          padding="goods"
          className="min-w-[41px] rounded-[3px] text-brand-neutral-60 self-stretch block design:hidden"
          onClick={() => {
            onClick?.(item);
          }}
        >
          <div className="text-sm font-medium whitespace-nowrap leading-[17px]">
            재고
            <br />
            변경
          </div>
        </CommonButton>
      </div>
    </div>
  );
};
